import { useCallback, useMemo, useState } from 'react';

import type { ProcessingItem } from './processing-model';
import { workingStates } from './processing-model';

export interface ProcessingCandidate extends ProcessingItem {
  trackWhenNew?: boolean;
}

interface TrackerState {
  scopeKey: string;
  initialized: boolean;
  known: string[];
  tracked: string[];
  dismissed: string[];
  observedCandidates: ProcessingCandidate[] | null;
  observedForcedKeys: ReadonlySet<string> | null;
}

const emptyState = (scopeKey: string): TrackerState => ({
  scopeKey,
  initialized: false,
  known: [],
  tracked: [],
  dismissed: [],
  observedCandidates: null,
  observedForcedKeys: null,
});

export function useTrackedProcessingItems(
  scopeKey: string,
  candidates: ProcessingCandidate[],
  ready: boolean,
  forcedKeys: ReadonlySet<string>,
) {
  const [state, setState] = useState<TrackerState>(() => emptyState(scopeKey));

  if (state.scopeKey !== scopeKey) {
    setState(emptyState(scopeKey));
  } else if (
    ready
    && (state.observedCandidates !== candidates || state.observedForcedKeys !== forcedKeys)
  ) {
    const candidateKeys = candidates.map((item) => item.key);
    const workingKeys = candidates
      .filter((item) => workingStates.has(item.state))
      .map((item) => item.key);
    const forced = candidates.filter((item) => forcedKeys.has(item.key)).map((item) => item.key);

    if (!state.initialized) {
      setState({
        ...state,
        initialized: true,
        known: candidateKeys,
        tracked: [...new Set([...workingKeys, ...forced])],
        observedCandidates: candidates,
        observedForcedKeys: forcedKeys,
      });
    } else {
      const known = new Set(state.known);
      const newlyTrackable = candidates
        .filter((item) => item.trackWhenNew && !known.has(item.key))
        .map((item) => item.key);
      setState({
        ...state,
        known: [...new Set([...state.known, ...candidateKeys])],
        tracked: [...new Set([...state.tracked, ...workingKeys, ...forced, ...newlyTrackable])],
        dismissed: state.dismissed.filter((key) => !workingKeys.includes(key)),
        observedCandidates: candidates,
        observedForcedKeys: forcedKeys,
      });
    }
  }

  const visibleItems = useMemo(() => {
    if (state.scopeKey !== scopeKey) return [];
    const byKey = new Map(candidates.map((item) => [item.key, item]));
    return state.tracked
      .filter((key) => !state.dismissed.includes(key))
      .map((key) => byKey.get(key))
      .filter((item): item is ProcessingCandidate => !!item);
  }, [candidates, scopeKey, state]);

  const dismissAll = useCallback(() => {
    setState((current) => ({
      ...current,
      dismissed: [...new Set([...current.dismissed, ...visibleItems.map((item) => item.key)])],
    }));
  }, [visibleItems]);

  return { items: visibleItems, dismissAll };
}
